import type { Gates } from 'lib/statsig';
import { FeatureFlags } from 'lib/types/types';
import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { empty } from '@apollo/client';
import { logger } from '@finance/shared';

type GlobalGates = {
    gates: Gates;
    setGates: (gates: Gates) => void;
    contextInTree: boolean;
};

const GatesContext = createContext<GlobalGates>({ gates: {}, contextInTree: false, setGates: empty } as GlobalGates);

export const useGateOpen = (gate: FeatureFlags) => {
    const { gates } = useContext(GatesContext);
    if (!gates) {
        throw new Error('useGateOpen must be used within a GatesProvider');
    }

    return !!gates[gate];
};

export const useGatesOpen = (gates: FeatureFlags[]) => {
    const { gates: allGates } = useContext(GatesContext);
    if (!allGates) {
        throw new Error('useGatesOpen must be used within a GatesProvider');
    }

    return gates.reduce<Gates>((acc, gate) => {
        acc[gate] = !!allGates[gate];
        return acc;
    }, {} as Gates);
};

export type GatesProviderProps = PropsWithChildren<{ gates: Gates }>;

export const GatesProvider: FC<{ initialGates?: Gates }> = ({ children, initialGates }) => {
    const [gates, setGates] = useState<Gates>(initialGates || {});

    return <GatesContext.Provider value={{ gates: gates, setGates, contextInTree: true }}>{children}</GatesContext.Provider>;
};

// Since gates are fetched in SSR, and since we dont want to have a GSSP on _app, we need to inject the gates lower in the chain
// but we still want them to be available in GlobalContext
export const GatesSetter: FC<GatesProviderProps> = ({ children, gates }) => {
    const { setGates, contextInTree } = useContext(GatesContext);

    useEffect(() => {
        setGates && setGates(gates);
    }, [gates, setGates]);

    if (!contextInTree) {
        logger.warn('GatesSetter is used in a tree without a provider. This is not recommended and may cause issues.');
        return <GatesProvider initialGates={gates}>{children}</GatesProvider>;
    }

    return <>{children}</>;
};
